import React, { useMemo } from 'react';
import styles from '../SettingsManager.module.scss';
import { AppSettings } from '../../../types';
import { tokens } from '../../../styles/tokens';

interface ThemeColors {
  textColor: string;
  accentColor: string;
  secondaryText: string;
}

interface TestPreviewSelectorProps {
  settings: AppSettings;
  onSettingsChange: (settings: AppSettings) => void;
  themeColors?: ThemeColors;
}

const formatMinutes = (minutes: number): string => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  const period = h >= 12 ? 'PM' : 'AM';
  const displayH = h % 12 || 12;
  return `${displayH}:${String(m).padStart(2, '0')} ${period}`;
};

export const TestPreviewSelector: React.FC<TestPreviewSelectorProps> = ({
  settings,
  onSettingsChange,
  themeColors,
}) => {
  const active = !!settings.testMode;

  // Default the preview to the current wall-clock time the first time it's used.
  const nowMinutes = useMemo(() => {
    const now = new Date();
    return now.getHours() * 60 + now.getMinutes();
  }, []);
  const minutes = settings.testPreviewMinutes ?? nowMinutes;

  const labelStyle: React.CSSProperties = themeColors ? { color: themeColors.textColor } : {};
  const descStyle: React.CSSProperties = themeColors ? { color: themeColors.secondaryText } : {};

  const toggleActive = () => {
    onSettingsChange({
      ...settings,
      testMode: !active,
      // Lock in a starting time when first activating
      testPreviewMinutes: settings.testPreviewMinutes ?? nowMinutes,
    });
  };

  return (
    <div className={styles.section}>
      <label className={styles.label} style={labelStyle}>
        Test Preview
      </label>
      <p className={styles.description} style={descStyle}>
        Preview how the clock looks at any time of day. Turn it on, then drag the
        time slider on the Clock screen to scrub through the day. Turn it off to go
        back to the real time.
      </p>

      <div className={styles.toggleRow}>
        <span className={styles.settingLabel} style={labelStyle}>
          {active ? `Previewing ${formatMinutes(minutes)} — adjust on the Clock screen` : 'Off — using the real time'}
        </span>
        <button
          type="button"
          className={`${styles.toggleButton} ${active ? styles.active : ''}`}
          style={{ background: active ? (themeColors?.accentColor || tokens.teal) : 'rgba(128,128,128,0.3)' }}
          onClick={toggleActive}
          aria-pressed={active}
          aria-label="Toggle test preview"
        >
          <span className={styles.toggleHandle} />
        </button>
      </div>
    </div>
  );
};
